import {
  FocusEventHandler,
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Combobox } from '@base-ui/react/combobox';

import { useAriaId } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { useTrackMenuOpen } from '@stores/menu-open-store';
import { pxToRem } from '@utils/converters';

import { ADD_OPTION_VALUE, AddOptionItem, isAddOptionItem } from './add-option';
import { createFilter } from './filter';
import { highlightBehaviorProps, isSameOptionValue, optionLabelToString } from './highlight';
import { useSelectTranslations } from './locale';
import { NoOptions, OptionContent } from './option-content';
import { ResultCount, usePickAnnouncement } from './result-count';
import { SingleValueControl } from './single-value-control';
import {
  AddOptionLink,
  Backdrop,
  EmptyMessage,
  Popup,
  Portal,
  Positioner,
  VirtualList,
  WIDE_POPUP_WIDTH,
} from './styles';
import { useEchoedSelectionInput } from './use-echoed-selection-input';
import { OptionVirtualizer, scrollHighlightIntoView, scrollIndexToCenter } from './virtualized';
import { VirtualizedOptions } from './virtualized-options';

// A non-string label stringifies to '' for both the default filter and the
// input's echo of the selection, so it must bring filterText as its
// plain-text stand-in — enforced by the union.
export type SelectOptionLabel =
  | { label: string; filterText?: string }
  | { label: ReactNode; filterText: string };

export type SelectOption<T = string> = {
  value: T;
  helpText?: string;
  isDisabled?: boolean;
  // Extra information shown when hovering the option row, e.g. why it is
  // disabled. Not for repeating the label: rows wrap instead of truncating.
  tooltip?: string;
} & SelectOptionLabel;

type SelectPopupWidth = 'anchor' | 'wide' | number;

export type SelectProps<TOption extends SelectOption = SelectOption> = {
  options: TOption[];
  defaultValue?: string | TOption | null;
  value?: string | TOption | null;
  onChange?: (value: string) => void;
  onSelectionChange?: (option: TOption | null) => void;
  onBlur?: FocusEventHandler;
  placeholder?: string;
  noOptions?: (obj: { inputValue: string }) => string | null;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  labelIconAfter?: ReactNode;
  isDisabled?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  isAutoFocused?: boolean;
  prefix?: string;
  parseSingleValue?: (option: TOption) => ReactNode;
  error?: string;
  helpText?: string;
  menuPortalTarget?: HTMLElement;
  hasFilterText?: boolean;
  id?: string;
  // Anchor-sized by default; 'wide' gives the popup a stable, viewport-capped
  // comfortable width (320px). A number sets a custom minimum width in px —
  // prefer 'wide' unless the options provably don't fit it. Long labels wrap
  // in every mode.
  popupWidth?: SelectPopupWidth;
  hideLabel?: boolean;
  description?: string;
  addOption?: {
    label: string;
    href: string;
  };
};

const resolveOption = <TOption extends SelectOption>(
  value: string | TOption | null | undefined,
  options: TOption[],
): TOption | null => {
  if (value == null) return null;
  if (typeof value === 'object') {
    // Consumers pass reconstructed option objects (e.g. persisted form
    // values) whose label can be stale; the canonical options entry carries
    // the current label, which may be rich JSX, so the control renders that.
    return options.find((opt) => opt.value === value.value) ?? value;
  }
  return options.find((opt) => opt.value === value) ?? null;
};

// A rebuilt options array hands in new objects, and a JSX label is a new
// element every time, so options are the same when their value and the text
// the input echoes for them match.
const isSameOption = <TOption extends SelectOption>(
  left: TOption | null,
  right: TOption | null,
  toText: (option: TOption) => string,
) => {
  if (left === right) return true;
  if (!left || !right) return false;
  return left.value === right.value && toText(left) === toText(right);
};

// base-ui rewrites the input to the selection's label whenever the selection
// object changes identity, which wipes a query being typed; an equivalent
// option from a rebuilt options array keeps the previous object.
const useStableSelection = <TOption extends SelectOption>(
  next: TOption | null,
  toText: (option: TOption) => string,
) => {
  const selectionRef = useRef(next);
  if (!isSameOption(selectionRef.current, next, toText)) selectionRef.current = next;
  return selectionRef.current;
};

// A parseSingleValue rendering has no plain text of its own to repeat, unlike
// the option's own label.
const toShownValueText = (option: SelectOption, shownValue: ReactNode) => {
  if (typeof shownValue === 'string') return shownValue;
  return shownValue === option.label ? optionLabelToString(option) : undefined;
};

const resolvePopupMinWidth = (popupWidth: SelectPopupWidth) => {
  switch (popupWidth) {
    case 'anchor':
      return undefined;
    case 'wide':
      return WIDE_POPUP_WIDTH;
    default:
      return pxToRem(popupWidth);
  }
};

const defaultOptionFilter = createFilter<SelectOption>();

const filterTextOptionFilter = createFilter<SelectOption>({
  stringify: ({ data }) => data.filterText ?? '',
});

export function Select<TOption extends SelectOption = SelectOption>(props: SelectProps<TOption>) {
  const { t } = useSelectTranslations();
  const {
    options,
    value,
    defaultValue,
    onChange,
    onSelectionChange,
    onBlur,
    placeholder = t('placeholder'),
    noOptions = () => t('no_options'),
    label,
    labelIcon,
    labelIconAfter,
    isDisabled = false,
    isSearchable = true,
    isClearable = true,
    isAutoFocused = false,
    prefix,
    parseSingleValue,
    error = '',
    helpText = '',
    menuPortalTarget,
    id: providedId,
    hideLabel,
    hasFilterText,
    addOption,
    description,
    popupWidth,
  } = props;

  const selectId = useAriaId('select');
  // Surface the consumer-provided id on the actual input so `#id` selectors
  // and `<label htmlFor>` resolve — base-ui's Root renders no DOM element.
  const inputId = providedId ?? selectId;
  const isControlled = value !== undefined;
  const virtualizerRef = useRef<OptionVirtualizer | null>(null);
  const highlightedIndexRef = useRef(-1);
  const hasScrolledOnOpenRef = useRef(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  // After the first pick/clear the user owns the uncontrolled selection.
  const hasInteractedRef = useRef(false);

  const itemToStringLabel = useCallback(
    (item: TOption | AddOptionItem) => {
      if (isAddOptionItem(item)) return item.label;
      if (parseSingleValue) {
        const rendered = parseSingleValue(item);
        if (typeof rendered === 'string') return rendered;
      }
      return optionLabelToString(item) || String(item.value);
    },
    [parseSingleValue],
  );

  const [localSelected, setLocalSelected] = useState<TOption | null>(() =>
    resolveOption(defaultValue, options),
  );
  const resolvedValue = isControlled ? resolveOption(value, options) : null;
  const controlledSelected = useStableSelection(resolvedValue, itemToStringLabel);
  const selected = isControlled ? controlledSelected : localSelected;
  const shownOption = isControlled ? resolvedValue : resolveOption(localSelected, options);

  const reconcileLocalSelected = useEvent(() => {
    const isAwaitingOptions = options.length === 0;
    const isShownOptionStillSelected =
      hasInteractedRef.current ||
      localSelected?.value ===
        (typeof defaultValue === 'object' ? defaultValue?.value : defaultValue);
    if (isAwaitingOptions && isShownOptionStillSelected) return;
    const next = hasInteractedRef.current
      ? (options.find((opt) => opt.value === localSelected?.value) ?? null)
      : resolveOption(defaultValue, options);
    if (isSameOption(next, localSelected, itemToStringLabel)) return;
    setLocalSelected(next);
    // The consumer's value would otherwise still hold the dropped option.
    const isShownOptionDropped =
      localSelected !== null &&
      !isAwaitingOptions &&
      !options.some((opt) => opt.value === localSelected.value);
    if (next === null && isShownOptionDropped) {
      onChange?.('');
      onSelectionChange?.(null);
    }
  });

  useEffect(() => {
    if (isControlled) return;
    reconcileLocalSelected();
  }, [options, defaultValue, isControlled, reconcileLocalSelected]);

  const shownValue = shownOption ? (parseSingleValue?.(shownOption) ?? shownOption.label) : null;
  const echo = useEchoedSelectionInput({
    hasSelection: Boolean(selected),
    isClearable,
    placeholder,
    selectedText: selected ? itemToStringLabel(selected) : null,
    overlayContent: shownValue,
  });

  const pick = usePickAnnouncement(echo.isOpen);

  const handleValueChange = useEvent(
    (next: TOption | AddOptionItem | null, details: Combobox.Root.ChangeEventDetails) => {
      // base-ui never selects a link item, so the add row only reaches here in
      // the type system.
      if (next && isAddOptionItem(next)) return;
      if (next === null && echo.keepsSelectionOnEmptiedInput(details.reason)) return;
      hasInteractedRef.current = true;
      if (!isControlled) setLocalSelected(next);
      echo.endEditing();
      onChange?.(next ? next.value : '');
      onSelectionChange?.(next);
      if (next) pick.announcePick(itemToStringLabel(next));
      if (!next && selected) pick.announceRemoval(itemToStringLabel(selected));
      if (next) echo.blurAfterPickOnTouchScreen(details.event);
    },
  );

  const handleOpenChange = useEvent((open: boolean, details?: Combobox.Root.ChangeEventDetails) => {
    // A press on the backdrop closes on its click, so the whole press lands
    // there: a react-modal host clears its close guard only on a click.
    if (
      !open &&
      details?.reason === 'outside-press' &&
      details.event.target === backdropRef.current
    ) {
      details.cancel();
      return;
    }
    if (open) hasScrolledOnOpenRef.current = false;
    echo.handleOpenChange(open);
  });

  const handleInputValueChange = useEvent(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      const isQueryEdit = echo.handleInputValueChange(next, details);
      if (isQueryEdit) {
        pick.clearPick();
        virtualizerRef.current?.scrollToOffset(0);
      }
    },
  );

  useTrackMenuOpen(echo.isOpen);

  const addOptionLabel = addOption?.label;
  const addOptionHref = addOption?.href;
  const addOptionItem = useMemo<AddOptionItem | null>(
    () =>
      addOptionLabel !== undefined && addOptionHref !== undefined
        ? { value: ADD_OPTION_VALUE, label: addOptionLabel, href: addOptionHref }
        : null,
    [addOptionLabel, addOptionHref],
  );
  const items = useMemo<(TOption | AddOptionItem)[]>(
    () => (addOptionItem ? [...options, addOptionItem] : options),
    [options, addOptionItem],
  );

  const optionFilter = hasFilterText ? filterTextOptionFilter : defaultOptionFilter;
  const effectiveFilter = useCallback(
    (item: TOption | AddOptionItem, query: string) =>
      isAddOptionItem(item) || optionFilter(item, query),
    [optionFilter],
  );

  const emptyMessage = noOptions({ inputValue: echo.query });
  const resolvedPopupWidth = popupWidth ?? 'anchor';
  const LabelWrapper = hideLabel ? VisuallyHidden : Fragment;

  return (
    <Stack space={1}>
      <LabelWrapper>
        <FormLabel htmlFor={inputId} text={label} icon={labelIcon} iconAfter={labelIconAfter} />
      </LabelWrapper>
      <FieldDescription text={description} />
      <Combobox.Root<TOption | AddOptionItem, false>
        items={items}
        value={selected}
        onValueChange={handleValueChange}
        open={echo.isOpen}
        onOpenChange={handleOpenChange}
        onInputValueChange={handleInputValueChange}
        filter={effectiveFilter}
        isItemEqualToValue={isSameOptionValue}
        itemToStringLabel={itemToStringLabel}
        disabled={isDisabled}
        virtualized
        onItemHighlighted={(highlighted, { reason, index }) => {
          highlightedIndexRef.current = index;
          const virtualizer = virtualizerRef.current;
          if (!virtualizer) return;
          // The add row sits past the virtual rows and is always in view.
          if (index < 0 || index >= virtualizer.options.count) return;
          if (reason === 'keyboard') {
            scrollHighlightIntoView(virtualizer, index);
            return;
          }
          // autoHighlight keeps re-emitting 'none' highlights while hovering or typing, so acting
          // on anything else would snap the list to the top mid-interaction.
          if (
            reason !== 'none' ||
            hasScrolledOnOpenRef.current ||
            !selected ||
            highlighted?.value !== selected.value
          ) {
            return;
          }
          hasScrolledOnOpenRef.current = true;
          queueMicrotask(() => {
            if (virtualizerRef.current) scrollIndexToCenter(virtualizerRef.current, index);
          });
        }}
        {...highlightBehaviorProps}
      >
        <SingleValueControl
          inputId={inputId}
          placeholder={placeholder}
          prefix={prefix}
          echo={echo}
          valueText={shownOption ? toShownValueText(shownOption, shownValue) : undefined}
          hasSelection={Boolean(selected)}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isSearchable={isSearchable}
          isAutoFocused={isAutoFocused}
          hasError={Boolean(error)}
          onBlur={onBlur}
          onOpen={() => handleOpenChange(true)}
        />
        <ResultCount
          isAnnouncing={echo.isOpen}
          pickedLabel={pick.pickedLabel}
          removedLabel={pick.removedLabel}
        />

        <Portal container={menuPortalTarget}>
          <Backdrop ref={backdropRef} onClick={() => handleOpenChange(false)} />
          <Positioner $minWidthRem={resolvePopupMinWidth(resolvedPopupWidth)} align="start">
            <Popup $isHiddenWhenEmpty={emptyMessage === null}>
              {emptyMessage !== null &&
                (addOptionItem ? (
                  <TrailingRowEmpty>{emptyMessage}</TrailingRowEmpty>
                ) : (
                  <NoOptions>{emptyMessage}</NoOptions>
                ))}
              <VirtualList>
                <VirtualizedOptions
                  virtualizerRef={virtualizerRef}
                  highlightedIndexRef={highlightedIndexRef}
                  renderOption={(item: SelectOption) => (
                    <OptionContent
                      label={item.label}
                      helpText={item.helpText}
                      tooltip={item.tooltip}
                    />
                  )}
                />
                {addOptionItem && (
                  <AddOptionRow item={addOptionItem} onActivate={() => handleOpenChange(false)} />
                )}
              </VirtualList>
            </Popup>
          </Positioner>
        </Portal>
      </Combobox.Root>
      {(error || helpText) && (
        <div>
          <FormError text={error} />
          {helpText && <Text font="regular14" color="textHelper" text={helpText} />}
        </div>
      )}
    </Stack>
  );
}

// With an add row the filtered list is never empty, so base-ui's Empty never
// fires; count the real options instead.
const TrailingRowEmpty = ({ children }: { children: ReactNode }) => {
  const hasOptions = Combobox.useFilteredItems<SelectOption>().some(
    (item) => !isAddOptionItem(item),
  );
  return (
    <div role="status" aria-live="polite" aria-atomic>
      {!hasOptions && <EmptyMessage>{children}</EmptyMessage>}
    </div>
  );
};

const AddOptionRow = ({ item, onActivate }: { item: AddOptionItem; onActivate: () => void }) => {
  // Appended last, so always the final filtered item.
  const setSize = Combobox.useFilteredItems().length;
  return (
    <AddOptionLink
      index={setSize - 1}
      value={item}
      aria-posinset={setSize}
      aria-setsize={setSize}
      // The input owns focus (aria-activedescendant), so the link stays out of
      // the Tab order — and, since floating-ui focuses a clicked item, out of
      // base-ui's click handling too. base-ui leaves external links open, so
      // the row closes the popup itself; the link's native navigation does
      // the rest.
      render={<a href={item.href} target="_blank" rel="noopener noreferrer" tabIndex={-1} />}
      onClick={(event) => {
        event.preventBaseUIHandler();
        onActivate();
      }}
    >
      <Stack orientation="horizontal" align="center" space={1}>
        <IconAddPlus size={24} color="elementPurple80" />
        <Text font="regular14" color="textPrimary" text={item.label} />
        <IconOpenNewTab size={16} color="elementPurple50" />
      </Stack>
    </AddOptionLink>
  );
};
